import { useEffect, useMemo, useRef, useState } from 'react';
import ExerciseCard from './ExerciseCard.jsx';

function combineHistory(chats) {
  const all = (chats || []).flatMap((c) => c.messages || []);
  return all.slice().sort((a, b) => (a.ts || 0) - (b.ts || 0));
}

export default function ChatPanel({ data, chat, online }) {
  const seeded = useRef(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [activeExercise, setActiveExercise] = useState(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (seeded.current) return;
    if (!data || !data.chats) return;
    setMessages(combineHistory(data.chats));
    seeded.current = true;
  }, [data]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, activeExercise]);

  async function send(text) {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    const ts = Date.now();
    const userMsg = { role: 'user', content: trimmed, ts };
    const placeholder = { role: 'assistant', content: '', ts: ts + 1, pending: true };
    setMessages((prev) => [...prev, userMsg, placeholder]);
    setInput('');
    setSending(true);
    try {
      const res = await chat(trimmed);
      const assistantMsg = {
        role: 'assistant',
        content: res?.message ?? '',
        exercise: res?.exercise || null,
        ts: res?.ts || ts + 1
      };
      setMessages((prev) => prev.slice(0, -1).concat(assistantMsg));
      if (res?.exercise) setActiveExercise({ ...res.exercise, msgTs: assistantMsg.ts });
    } catch (err) {
      console.error(err);
      const errorMsg = {
        role: 'assistant',
        content: online ? 'Hm. Something broke on my end. Try that again?' : "We're offline. I'll catch up when we're back.",
        ts: Date.now(),
        error: true
      };
      setMessages((prev) => prev.slice(0, -1).concat(errorMsg));
    } finally {
      setSending(false);
    }
  }

  function onSubmit(e) {
    e.preventDefault();
    send(input);
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e);
    }
  }

  function handleExerciseComplete(summary) {
    setActiveExercise(null);
    send(summary);
  }

  return (
    <section className="chat-panel">
      <header className="chat-panel__header">
        <h2>Eliza</h2>
        {!online && <span className="chat-panel__offline">offline</span>}
      </header>
      <div
        className="chat-panel__messages"
        ref={scrollRef}
        role="log"
        aria-label="Conversation with Eliza"
        tabIndex={0}
      >
        {messages.length === 0 && !sending && (
          <div className="chat-panel__empty">
            <p>What's on your mind?</p>
          </div>
        )}
        {messages.map((m, i) => (
          <Message key={`${m.ts}-${i}`} message={m} />
        ))}
        {activeExercise && (
          <ExerciseCard
            exercise={activeExercise}
            onComplete={handleExerciseComplete}
            onCancel={() => setActiveExercise(null)}
          />
        )}
      </div>
      <form className="chat-panel__composer" onSubmit={onSubmit}>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Say something…"
          aria-label="Message Eliza"
          rows={1}
          maxLength={4000}
          disabled={sending || Boolean(activeExercise)}
        />
        <button
          type="submit"
          className="chat-panel__send"
          disabled={!input.trim() || sending || Boolean(activeExercise)}
          aria-label="Send"
        >
          ↑
        </button>
      </form>
    </section>
  );
}

function Message({ message }) {
  const cls = `chat-bubble chat-bubble--${message.role}${message.error ? ' chat-bubble--error' : ''}`;
  if (message.pending) {
    return (
      <div className={cls}>
        <span className="chat-bubble__dots" aria-hidden="true">
          <span /> <span /> <span />
        </span>
        <span className="sr-only">Eliza is typing</span>
      </div>
    );
  }
  if (!message.content) return null;
  return <div className={cls}>{message.content}</div>;
}
